import React, { useEffect, useRef, useState } from "react";
import { Joyride, EVENTS, ORIGIN } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";

const STORAGE_KEY = "argus.tour.completed-v1";

const steps = [
  {
    target: '[data-testid="app-topbar"]',
    title: "Welcome to ArgusSecure.",
    content:
      "Quick tour — 60 seconds. You'll see how to scan, triage findings, and use the embedded AI engineer. Skip anytime.",
    placement: "bottom",
    skipBeacon: true,
  },
  {
    target: '[data-testid="sidebar-projects"]',
    title: "Projects",
    content:
      "Every codebase, app URL, or dependency manifest you scan lives here. Demo projects are pre-loaded so you can poke around.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: '[data-testid="sidebar-scans"]',
    title: "Scans",
    content:
      "SAST, DAST, and SCA scans run in the background. Filter by status, scanner, and project — perfect for CI debugging.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: '[data-testid="sidebar-findings"]',
    title: "Findings",
    content:
      "Every issue ArgusSecure finds, in one queue. Server-side filters: severity, scanner, project, free-text search.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: '[data-testid="sidebar-reports"]',
    title: "Reports",
    content: "PDF reports for audits + executive summaries. Branded, signed, ready to send.",
    placement: "right",
    skipBeacon: true,
  },
  {
    target: '[data-testid="sidebar-settings"]',
    title: "Settings & Billing",
    content:
      "Change password, manage API tokens, view audit log, and upgrade to Pro from one place.",
    placement: "right",
    skipBeacon: true,
  },
];

// Joyride styling that matches the classical theme.
const tourStyles = {
  options: {
    arrowColor: "rgb(var(--panel))",
    backgroundColor: "rgb(var(--panel))",
    primaryColor: "rgb(var(--gold))",
    textColor: "rgb(var(--ink))",
    overlayColor: "rgba(0, 0, 0, 0.65)",
    zIndex: 10000,
  },
  tooltip: {
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    borderRadius: 8,
    border: "1px solid rgb(var(--line))",
    padding: "20px 22px",
    backgroundColor: "rgb(var(--panel))",
  },
  tooltipTitle: {
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: 24,
    margin: 0,
    color: "rgb(var(--ink))",
    textAlign: "left",
    fontWeight: 500,
  },
  tooltipContent: {
    fontSize: 14,
    fontFamily: "Inter, system-ui, sans-serif",
    color: "rgb(var(--ink) / 0.85)",
    lineHeight: 1.55,
    padding: "10px 0 4px",
    textAlign: "left",
  },
  buttonNext: {
    backgroundColor: "rgb(var(--gold))",
    color: "rgb(var(--paper))",
    fontWeight: 600,
    borderRadius: 4,
    padding: "9px 18px",
    fontSize: 13,
    letterSpacing: "0.02em",
  },
  buttonBack: { color: "rgb(var(--ink))", marginRight: 8, fontSize: 13, opacity: 0.7 },
  buttonSkip: { color: "rgb(var(--ink))", opacity: 0.5, fontSize: 12 },
  buttonClose: { color: "rgb(var(--ink))", opacity: 0.5 },
  spotlight: { borderRadius: 6 },
};

const locale = {
  back: "Back",
  close: "Close",
  last: "Got it",
  next: "Next",
  skip: "Skip tour",
};

export default function GuidedTour() {
  const { user } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);
  const navigatedAfterFinishRef = useRef(false);

  // Decide once on /app load whether to autostart.
  useEffect(() => {
    if (!user) return;
    if (loc.pathname !== "/app" && loc.pathname !== "/app/welcome") return;
    const done = localStorage.getItem(STORAGE_KEY);
    if (done) return;
    const t = setTimeout(() => setRun(true), 700);
    return () => clearTimeout(t);
  }, [user, loc.pathname]);

  // Manually triggerable from Welcome / help menu
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(STORAGE_KEY);
      navigatedAfterFinishRef.current = false;
      setRun(true);
    };
    window.addEventListener("argus:start-tour", handler);
    return () => window.removeEventListener("argus:start-tour", handler);
  }, []);

  // react-joyride v3: use `onEvent` instead of `callback`.
  const onEvent = (data) => {
    const { type, origin } = data || {};

    // X close button OR Skip button — both terminate the tour for good.
    if (type === EVENTS.TOUR_END || origin === ORIGIN.BUTTON_CLOSE || origin === ORIGIN.BUTTON_SKIP) {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
      setRun(false);
      // Only auto-nav if the user reached the last step (origin will be BUTTON_PRIMARY then).
      if (type === EVENTS.TOUR_END && origin === "button_primary" && !navigatedAfterFinishRef.current) {
        navigatedAfterFinishRef.current = true;
        navigate("/app/projects");
      }
      return;
    }

    // Target missing — bail gracefully (e.g., sidebar collapsed).
    if (type === EVENTS.TARGET_NOT_FOUND) {
      setRun(false);
    }
  };

  if (!user) return null;

  return (
    <Joyride
      run={run}
      steps={steps}
      onEvent={onEvent}
      continuous
      showSkipButton
      showProgress
      disableScrolling
      hideCloseButton={false}
      locale={locale}
      styles={tourStyles}
      floaterProps={{ disableAnimation: false }}
    />
  );
}

/**
 * Imperatively start the tour (e.g., from a "Replay tour" button).
 */
export function startGuidedTour() {
  window.dispatchEvent(new CustomEvent("argus:start-tour"));
}
